import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaHtml5, FaCss3Alt, FaJsSquare, FaNodeJs, FaSearch, FaDatabase
} from 'react-icons/fa';
import {
  SiTailwindcss, SiExpress, SiMongodb, SiPostman, SiOpenai
} from 'react-icons/si';

// Reusable TransitionEffect component with animated gradient
const TransitionEffect = ({ isInView }) => (
  <motion.div
    className="absolute inset-0 opacity-0 bg-gradient-to-r from-[#ff4d5a] via-[#ff7d85] to-[#0d2f55] dark:from-[#ff7d85] dark:to-[#272729]"
    initial={{ opacity: 0 }}
    animate={isInView ? { opacity: 0.5, rotate: 360 } : { opacity: 0 }}
    whileHover={{ opacity: 0.8, scale: 1.05 }}
    transition={{ duration: 1.2, ease: "easeInOut" }}
  />
);

function Skills() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const staggerContainer = {
    visible: {
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const { ref: imageRef, inView: isImageInView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  return (
    <motion.section
      className="relative flex flex-col md:flex-row justify-between items-center p-4 sm:p-6 md:p-8 lg:p-16 min-h-screen overflow-hidden"
      initial="hidden"
      whileInView="visible"
      variants={staggerContainer}
    >
      {/* Profile Image for smaller screens */}
      <motion.div
        className="relative right w-full md:hidden mt-4 mb-4 lg:mt-0 z-10 hover:scale-110 hover:rotate-6 transition-transform"
        whileHover={{ scale: 1.1, rotate: 4 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="relative w-full h-full">
          <img
            ref={imageRef}
            src="/Image/Skill_Set.webp"
            className="rounded-lg w-full h-72 sm:h-96 object-cover shadow-2xl transform hover:scale-105 transition-transform duration-500"
            alt="Profile"
          />
          <TransitionEffect isInView={isImageInView} />
          <div className="absolute inset-0 bg-[#072142] opacity-10"></div>
          <div className="absolute inset-0 bg-[#ff4d5a] opacity-5"></div>

          {/* Skill icons that pop up */}
          <motion.div
            className="absolute inset-0 flex justify-center items-center flex-wrap gap-6"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={isImageInView ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", staggerChildren: 0.2 }}
          >
            <FaHtml5 className="text-red-500 text-5xl hover:animate-bounce" />
            <FaCss3Alt className="text-blue-500 text-5xl hover:animate-pulse" />
            <FaJsSquare className="text-yellow-500 text-5xl hover:animate-spin" />
            <SiTailwindcss className="text-teal-500 text-5xl hover:animate-ping" />
            <FaNodeJs className="text-green-500 text-5xl hover:animate-bounce" />
            <SiExpress className="text-gray-500 text-5xl hover:animate-pulse" />
            <SiMongodb className="text-green-600 text-5xl hover:animate-ping" />
            <SiPostman className="text-orange-500 text-5xl hover:animate-spin" />
            <SiOpenai className="text-gray-900 text-5xl hover:animate-bounce" />
            <FaSearch className="text-purple-500 text-5xl hover:animate-pulse" />
            <FaDatabase className="text-rose-950 text-5xl hover:animate-spin" />
          </motion.div>
        </div>
      </motion.div>

      {/* Text Section */}
      <motion.div
        className="w-full md:w-1/2 flex flex-col items-start justify-center py-6 sm:py-10 z-10"
        variants={fadeInUp}
      >
        <motion.h2
          className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-snug tracking-wide text-dark dark:text-light mb-6 transition-transform duration-300 hover:scale-110 hover:text-[#ff4d5a]"
          variants={fadeInUp}
        >
          Skills Set
        </motion.h2>

        {/* Divider Lines */}
        <motion.div className="flex items-center mb-6 w-full" variants={fadeInUp}>
          <span className="block w-16 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-[#ff4d5a] to-[#ff934f]"></span>
          <span className="block w-16 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] ml-6"></span>
        </motion.div>

        {/* Skill Categories with more animations */}
        <motion.div className="space-y-6 w-full" variants={staggerContainer}>
          {[{
              title: "Web Development Technologies",
              items: ["HTML, CSS, Tailwind CSS", "JavaScript and React.js"],
            },
            {
              title: "Back End Development",
              items: ["Node.js and Express.js", "MongoDB"],
            },
            {
              title: "API Development and Testing",
              items: ["REST APIs", "Open API & Postman"],
            },
            {
              title: "Search Engine Optimization (SEO)",
              items: ["I apply SEO strategies to enhance web applications for targeted audiences."],
            },
            {
              title: "Real Time Web",
              items: ["I develop real-time web applications that offer users instant updates."],
            },
          ].map((skill, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              whileHover={{ x: 15, scale: 1.05 }}
              className="group transition-transform"
            >
              <h3 className="font-semibold text-lg md:text-xl text-dark dark:text-light group-hover:text-[#ff4d5a] transition-colors cursor-pointer">
                {skill.title}
              </h3>
              <ul className="ml-5 mt-2 text-sm md:text-base text-dark dark:text-light space-y-1">
                {skill.items.map((item, i) => (
                  <li key={i} className="transition-opacity hover:opacity-75">
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Profile Image for larger screens */}
      <motion.div
        className="hidden md:block w-full md:w-1/2 relative mt-4 lg:mt-0 z-10"
        variants={fadeInUp}
        whileHover={{ scale: 1.1, rotate: 6 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="relative w-full h-full">
          <img
            ref={imageRef}
            src="/Image/Skill_Set.webp"
            className="rounded-lg w-full h-96 object-cover shadow-2xl transform hover:scale-110 transition-transform duration-500"
            alt="Profile"
          />
          <TransitionEffect isInView={isImageInView} />
          <div className="absolute inset-0 bg-[#072142] opacity-10"></div>
          <div className="absolute inset-0 bg-[#ff4d5a] opacity-5"></div>

          {/* Skill icons for larger screens */}
          <motion.div
            className="absolute inset-0 flex justify-center items-center flex-wrap gap-6"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={isImageInView ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", staggerChildren: 0.2 }}
          >
            <FaHtml5 className="text-red-500 text-5xl hover:animate-bounce" />
            <FaCss3Alt className="text-blue-500 text-5xl hover:animate-pulse" />
            <FaJsSquare className="text-yellow-500 text-5xl hover:animate-spin" />
            <SiTailwindcss className="text-teal-500 text-5xl hover:animate-ping" />
            <FaNodeJs className="text-green-500 text-5xl hover:animate-bounce" />
            <SiExpress className="text-gray-500 text-5xl hover:animate-pulse" />
            <SiMongodb className="text-green-600 text-5xl hover:animate-ping" />
            <SiPostman className="text-orange-500 text-5xl hover:animate-spin" />
            <SiOpenai className="text-gray-900 text-5xl hover:animate-bounce" />
            <FaSearch className="text-purple-500 text-5xl hover:animate-pulse" />
            <FaDatabase className="text-rose-950 text-5xl hover:animate-spin" />
          </motion.div>
        </div>
      </motion.div>
    </motion.section>
  );
}

export default Skills;
